import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { Meta } from "../components/SEO";
import { company } from "../data/company";
import { StatsBar, ProcessSection, CtaStrip } from "../components/Sections";
import { services } from "../data/services";
import { SmartImage } from "../components/SmartImage";

export default function OverOnsPage() {
  return (
    <>
      <Meta
        title="Over ons — Aannemer Maassluis, 15+ jaar ervaring | Koning"
        description="Familiebedrijf uit Maassluis met 15+ jaar praktijkervaring in renovatie en verbouwing. Vast uitvoeringsteam en een vaste prijs. Maak kennis met ons team."
      />

      <section className="relative min-h-[60vh] flex items-end overflow-hidden grain" data-testid="overons-hero">
        <SmartImage
          src="https://images.pexels.com/photos/13722886/pexels-photo-13722886.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=2000"
          alt="Koning Aannemersbedrijf"
          sizes="100vw"
          eager
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative w-full max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="eyebrow mb-5">Over Koning</div>
            <h1 className="font-display text-white text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              Bouwen is een <span className="italic text-koning-gold">vak</span>. <br />
              Wij beheersen het.
            </h1>
          </div>
        </div>
      </section>

      {/* INTRO — premium licht voor verhalende leesbaarheid */}
      <section className="py-20 md:py-28 bg-[#F5F2EA] border-b border-[#E1D9C5]" data-testid="overons-intro">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-2 gap-14">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-4">Ons verhaal</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight text-[#1A1816]">
              Een familie­bedrijf met <span className="italic text-[#8A7430]">15+ jaar praktijkervaring</span>
            </h2>
          </div>
          <div className="text-[#4A453E] leading-relaxed space-y-5 text-base md:text-lg">
            <p>
              Koning Aannemersbedrijf is een <span className="text-[#8A7430] font-medium">familiebedrijf</span> met meer dan 15 jaar praktijkervaring in renovatie en afbouw in Zuid-Holland. Wat begon als een kleine ploeg voor (dak)renovaties in Maassluis, is uitgegroeid tot een toonaangevende aannemer met een vast, ervaren team voor elk onderdeel van het bouwproces.
            </p>
            <p>
              Wij geloven dat een verbouwing meer is dan stenen stapelen. Het is uw thuis, uw rust, uw investering. Daarom werken wij volgens drie principes: <span className="text-[#8A7430] font-medium">vaste prijs</span>, <span className="text-[#8A7430] font-medium">een vast uitvoeringsteam</span> en <span className="text-[#8A7430] font-medium">één aanspreekpunt</span>. Voor specialistische onderdelen — denk aan installatiewerk, dakwerk of stukadoors — werken wij samen met een vast netwerk van <span className="text-[#8A7430] font-medium">vertrouwde specialistische partners</span> die wij persoonlijk kennen en waar wij al jaren op vertrouwen. Zo krijgt u altijd de juiste vakman op de juiste plek, zonder concessies aan kwaliteit.
            </p>
            <p>
              Wij realiseren renovatie- en verbouwingsprojecten in heel Zuid-Holland, van strakke badkamers tot complete villa-renovaties. Iedereen bij Koning weet: wij leveren pas op als wij er zelf trots op zijn.
            </p>
          </div>
        </div>
      </section>

      <StatsBar />

      <section className="py-20 md:py-28 bg-[#0B0B0B]" data-testid="overons-values">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="eyebrow mb-4">Onze waarden</div>
          <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight max-w-3xl">
            Premium betekent voor ons: <span className="italic text-koning-gold">geen compromissen</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              { t: "Vakmanschap", d: "Een vast uitvoeringsteam dat het verschil maakt — van de eerste tegel tot het laatste plintje." },
              { t: "Transparantie", d: "Vaste prijs, heldere planning, dagelijkse communicatie. Wijzigingen altijd vooraf schriftelijk afgestemd." },
              { t: "Garantie", d: "Wij staan achter de kwaliteit van ons werk met schriftelijke garantievoorwaarden bij oplevering. VCA-gecertificeerde medewerkers en KvK-geregistreerd." },
            ].map((v) => (
              <div key={v.t} className="border-t border-koning-gold pt-6">
                <div className="font-display text-koning-gold text-3xl">{v.t}</div>
                <p className="mt-3 text-koning-muted leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS — licht beige */}
      <section className="py-20 md:py-28 bg-[#F5F2EA] border-y border-[#E1D9C5]" data-testid="overons-certifications">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-4">Certificeringen & garanties</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight text-[#1A1816]">
                Officieel <span className="italic text-[#8A7430]">gecertificeerd</span>
              </h2>
              <p className="mt-5 text-[#6F6A60] max-w-md leading-relaxed">
                Wij zijn aangesloten bij toonaangevende keurmerken in de bouw — uw garantie voor kwaliteit, veiligheid en financiële zekerheid.
              </p>
            </div>
            <ul className="grid grid-cols-2 gap-4">
              {company.certifications.map((c) => (
                <li key={c} className="bg-white border border-[#E1D9C5] p-5 flex items-center gap-3 shadow-premium">
                  <Check className="text-[#8A7430]" size={18} />
                  <span className="text-[#1A1816] font-medium">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <ProcessSection items={services[0].process} />

      <CtaStrip variant="gold" title="Maak kennis met ons team" subtitle="Plan een vrijblijvende kennismaking op locatie of bij ons op kantoor." />
    </>
  );
}
